/**
 * Pure side-effect logic for the sign-up-code mint page. Extracted so it can
 * be unit-tested without a JSX renderer.
 *
 * Reads the sign-up code from session storage and consumes it (clears it)
 * immediately so an abandoned or failed mint never leaves stale state that
 * would re-route the customer back into the mint flow. It then obtains the
 * authenticated user's email from the ID token, calls the invite API silently,
 * and on success saves the returned tracking reference and redirects to
 * activate-track-id. Falls back to the generic sign-up entry on any error.
 */
import { ACTIVATE_TRACK_ID_PATH } from '@/pages/activate-track-id';
import { api } from '@/utils/api';
import {
  clearSignUpCodeFromStorage,
  getSignUpCodeFromStorage,
  saveTrackIdToStorage,
} from '@/utils/utils';

export async function runMintEffect(deps: {
  push: (path: string) => void;
  getTokensParsed: () => Promise<{ idToken?: { email?: string } } | null | undefined>;
}) {
  const code = getSignUpCodeFromStorage();
  if (!code) {
    // Nothing to consume.
    deps.push('/sign-up');
    return;
  }
  // The code is consumed by this page now. Clear it immediately, independent of
  // success, so an abandoned or failed mint does not leave stale state that
  // would re-route the customer back here through initialise-session.
  clearSignUpCodeFromStorage();

  const parsed = await deps.getTokensParsed();
  const email = parsed?.idToken?.email;
  if (!email) {
    deps.push('/sign-up');
    return;
  }

  try {
    const result = await api.postV1userinvite({ email, code, sendInvitationEmail: false });
    // `trackingReference` is optional in the generated schema. Fail closed if the
    // invite succeeded but returned no reference: do not store, do not proceed to
    // activate; send the customer back to the generic sign-up entry.
    const trackingReference = result.trackingReference;
    if (!trackingReference) {
      console.error('Mint from sign-up code returned no tracking reference:', result);
      deps.push('/sign-up');
      return;
    }
    saveTrackIdToStorage(trackingReference);
    deps.push(ACTIVATE_TRACK_ID_PATH);
  } catch (error) {
    console.error('Mint from sign-up code failed:', error);
    deps.push('/sign-up');
  }
}
